import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Fai, Plan, QUOTE_CONTEXTS, QuoteModel } from '@bytel/bytel-sales';
import { CommercialSeries, ConfigurationService, RangesNewGeneration } from '@bytel/checkout';

import { BannersService } from '@services/banners.service';
import { MainCartService } from '@services/main-cart.service';

import { DirectUsRepository } from '@repositories/directus.repository';

import {
    InfoWithPepitesItem,
    InsuranceCms,
    MixedCartMobileCms,
    PlanDetailsCms,
    WarningDeliveryMessages,
} from '@interfaces/directus-cms.interface';

import { Observable, map } from 'rxjs';

import { MixedCartService } from './mixed-cart.service';
import { PepitesService } from './pepites.service';

@Injectable({
    providedIn: 'root',
})
export class CmsService {
    protected directUsRepository: DirectUsRepository = inject(DirectUsRepository);
    protected bannersService: BannersService = inject(BannersService);
    protected pepitesService: PepitesService = inject(PepitesService);
    protected cartService: MainCartService = inject(MainCartService);
    protected mixedCartService: MixedCartService = inject(MixedCartService);
    protected http: HttpClient = inject(HttpClient);
    protected config: ConfigurationService = inject(ConfigurationService);

    public getInsuranceData(): Observable<InsuranceCms> {
        return this.directUsRepository.getInsuranceData();
    }

    public getMixedCartMobileData(
        quoteIndex: number = 0,
        isCompatiblePromoHome?: boolean,
    ): Observable<MixedCartMobileCms> {
        const quote = this.cartService.getQuote(quoteIndex);
        const slug = this.getMixedCartSlug(quote, isCompatiblePromoHome);

        return this.bannersService.getBannersForSlug(slug).pipe(
            map((banner) => ({
                title: banner.title,
                titleMobile: banner.title_mobile || banner.title,
                titleLogo: banner.title_logo,
                subtitle: banner.subtitle,
                mentions: banner.mentions,
                sticker: banner.sticker_value,
                stickerMobile: banner.sticker_value_mobile || banner.sticker_value,
                backgroundImg: banner.background_img,
                backgroundKv:
                    banner.background_choice_img_kv && banner.background_choice_img_kv !== 'background_choice_kv_other'
                        ? banner.background_choice_img_kv
                        : undefined,
                textColor: banner.inverted_content_desktop ? 'has-text-white' : 'has-text-tertiary',
                icon: banner.icon ? banner.icon[0] : null,
            })),
        );
    }

    public getPepitesBtbdOffer(slug: string): Observable<InfoWithPepitesItem> {
        return this.pepitesService.getBtbdPepites(slug);
    }

    public getPlanDetails(gencode: string, dataEnvelope: string, isRenewal: boolean): Observable<PlanDetailsCms> {
        return this.http.get<PlanDetailsCms>(`/webapi/cms/plan-details`, {
            params: {
                gencode,
                dataEnvelope,
                isRenewal: isRenewal.toString(),
            },
        });
    }

    public getDeliveryWarning(): Observable<WarningDeliveryMessages> {
        const urlApi = `${this.config.getData()?.global.contentEditor.checkoutApiBaseUrl}/delivery-message`;

        return this.http.get<WarningDeliveryMessages>(`${urlApi}`).pipe(
            map((data) => {
                const now = new Date();
                const items = data.items?.filter((warning) => {
                    const startDate = warning.start_date ? new Date(warning.start_date) : null;
                    const endDate = warning.end_date ? new Date(warning.end_date) : null;

                    if (startDate && startDate > now) {
                        return false;
                    }

                    if (endDate && endDate < now) {
                        return false;
                    }

                    return true;
                });
                return { items };
            }),
        );
    }

    public getMixedCartSlug(quote: QuoteModel, isCompatiblePromoHome?: boolean): string {
        let slug = '';
        if (quote?.context.id === QUOTE_CONTEXTS.ACQUISITIONFIX) {
            const faiInQuote = quote.getPrincipalProduct<Fai>('Fai');
            if (
                faiInQuote?.data.range_ng === RangesNewGeneration.VERY_FIT ||
                faiInQuote?.data.commercial_series === CommercialSeries.B_AND_YOU
            ) {
                return 'panier-mixte-offre-byou-pure-fibre';
            }
        }
        if (isCompatiblePromoHome) {
            slug =
                quote.context.id === QUOTE_CONTEXTS.ACQUISITION
                    ? 'panier-mixte-offre-foyer'
                    : 'panier-mixte-offre-foyer-fai';
        } else {
            const planInQuote = quote.getPrincipalProduct<Plan>('Plan');
            slug = quote.isAcquisition() ? 'panier-mixte-offre-fixe' : 'panier-mixte-offre-mobile';

            if (planInQuote instanceof Plan && this.mixedCartService.isCompatible(planInQuote, 'panenka')) {
                slug = 'panier-mixte-offre-panenka';
            }
        }
        return slug;
    }
}
